"use client";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchRecords,
  createRecord,
  updateRecord,
  deleteRecord,
  AirtableRecord,
  QueryOptions,
  TableRecordType,
  CROModule,
  StainingPlatformModule,
  ImageScanningModule,
  ImageAnalysisModule,
  CompanyModule,
  deserializeRecords,
  serialize,
  validateModel
} from './';
import { useEffect, useState } from 'react';
import { TableModelType } from './transform';

// Query key factory
const airtableKeys = {
  all: ['airtable'] as const,
  table: (table: string) => [...airtableKeys.all, table] as const,
  records: (table: string, options?: QueryOptions) => 
    [...airtableKeys.table(table), 'records', options] as const,
  record: (table: string, id: string) => 
    [...airtableKeys.table(table), 'record', id] as const,
  companyData: (companyName: string) => 
    [...airtableKeys.all, 'companyData', companyName] as const,
};

// --- All React Query hooks from hooks.ts go here ---

export function useAirtableRecords(
  tableId: string,
  options?: QueryOptions,
  queryOptions?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    transform?: boolean;
  }
) {
  const { transform = true, ...restQueryOptions } = queryOptions || {};
  return useQuery({
    queryKey: airtableKeys.records(tableId, options),
    queryFn: async () => {
      const response = await fetchRecords(tableId, options);
      if (transform) {
        return deserializeRecords(tableId, response.records) as TableModelType<string>[];
      }
      return response.records as TableRecordType<string>[];
    },
    enabled: queryOptions?.enabled ?? true,
    ...restQueryOptions,
  });
}

export function useAirtableRecord(
  tableId: string,
  recordId: string,
  queryOptions?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    transform?: boolean;
  }
) {
  const { transform = true, ...restQueryOptions } = queryOptions || {};
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: airtableKeys.record(tableId, recordId),
    queryFn: async () => {
      const cachedRecords = queryClient.getQueryData<TableRecordType<string>[]>(
        airtableKeys.records(tableId)
      );
      if (cachedRecords) {
        const cachedRecord = cachedRecords.find(record => record.id === recordId);
        if (cachedRecord) {
          if (transform) {
            return deserializeRecords(tableId, [cachedRecord])[0] as TableModelType<string>;
          }
          return cachedRecord;
        }
      }
      const response = await fetchRecords(tableId, {
        filterByFormula: `RECORD_ID() = '${recordId}'`,
        maxRecords: 1
      });
      if (response.records.length === 0) {
        throw new Error(`Record not found with ID: ${recordId}`);
      }
      if (transform) {
        return deserializeRecords(tableId, [response.records[0]])[0] as TableModelType<string>;
      }
      return response.records[0] as TableRecordType<string>;
    },
    enabled: queryOptions?.enabled ?? true,
    ...restQueryOptions,
  });
}

export function useCreateRecord(resolvedTableName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<TableModelType<string>>) => {
      const validatedData = validateModel(resolvedTableName, data);
      const fields = serialize(resolvedTableName, validatedData);
      const record = await createRecord(resolvedTableName, fields);
      return deserializeRecords(resolvedTableName, [record])[0] as TableModelType<string>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: airtableKeys.table(resolvedTableName)
      });
    }
  });
}

export function useUpdateRecord(resolvedTableName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TableModelType<string>> }) => {
      const validatedData = validateModel(resolvedTableName, data);
      const fields = serialize(resolvedTableName, validatedData);
      const record = await updateRecord(resolvedTableName, id, fields);
      return deserializeRecords(resolvedTableName, [record])[0] as TableModelType<string>;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: airtableKeys.table(resolvedTableName)
      });
      queryClient.invalidateQueries({
        queryKey: airtableKeys.record(resolvedTableName, variables.id)
      });
    }
  });
}

export function useDeleteRecord(resolvedTableName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return deleteRecord(resolvedTableName, id);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: airtableKeys.table(resolvedTableName)
      });
      queryClient.invalidateQueries({
        queryKey: airtableKeys.record(resolvedTableName, id)
      });
      queryClient.setQueryData<TableRecordType<string>[]>(
        airtableKeys.records(resolvedTableName),
        (oldData) => oldData?.filter(record => record.id !== id) || []
      );
    }
  });
}

export function useDynamicAirtableRecords(
  tableId: string,
  options?: Record<string, any>,
  queryOptions?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
  }
) {
  return useQuery({
    queryKey: ['dynamicAirtableRecords', tableId, options],
    queryFn: async () => {
      if (!tableId) return [];
      // Build query string from options (filters)
      let url = `/api/airtable?table=${encodeURIComponent(tableId)}`;
      if (options && typeof options === 'object') {
        const params = new URLSearchParams();
        Object.entries(options).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            params.append(key, String(value));
          }
        });
        if (params.toString()) {
          url += `&${params.toString()}`;
        }
      }
      // Fetch from our own API route, which handles schema and transformation server-side
      const res = await fetch(url);
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch Airtable data');
      }
      const data = await res.json();
      return data.records || [];
    },
    enabled: queryOptions?.enabled ?? true,
    ...queryOptions,
  });
}

/**
 * Hook to fetch consolidated company data (products, platforms, services, CROs) in one call
 * This replaces multiple separate API calls with a single optimized endpoint
 */
export function useConsolidatedCompanyData(
  companyName: string | undefined,
  queryOptions?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
  }
) {
  return useQuery({
    queryKey: airtableKeys.companyData(companyName || ''),
    queryFn: async () => {
      if (!companyName) return null;
      
      const url = `/api/airtable/company-data?companyName=${encodeURIComponent(companyName)}`;
      const res = await fetch(url);
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch consolidated company data');
      }
      
      const data = await res.json();
      return {
        products: data.products || [],
        platforms: data.platforms || [],
        services: data.services || [],
        cros: data.cros || [],
        metadata: data.metadata || {}
      };
    },
    enabled: queryOptions?.enabled ?? !!companyName,
    staleTime: queryOptions?.staleTime ?? 5 * 60 * 1000, // 5 minutes default
    refetchOnWindowFocus: queryOptions?.refetchOnWindowFocus ?? false,
    ...queryOptions,
  });
}

// ... Table-specific React Query hooks (useCROs, useStainingPlatforms, etc.) can also be moved here if needed ... 
